/**
 * Desktop sidebar collapse state (icon rail vs expanded), persisted per
 * browser. Pure helpers over a `Storage`-like argument so they run in node
 * tests without a window. A missing, blocked or garbage value reads as
 * expanded — the rail is an opt-in.
 */
export const SIDEBAR_STORAGE_KEY = 'tf-sidebar';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readCollapsed(storage: StorageLike | undefined): boolean {
  try {
    return storage?.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

export function writeCollapsed(storage: StorageLike | undefined, collapsed: boolean): void {
  try {
    storage?.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
  } catch {
    // Blocked storage (private window): the state just does not survive a reload.
  }
}

export function nextCollapsed(collapsed: boolean): boolean {
  return !collapsed;
}

interface ShortcutEventLike {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  target: EventTarget | null;
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** ⌘B / Ctrl+B, except while the user types into a field. */
export function isSidebarShortcut(event: ShortcutEventLike): boolean {
  if (event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey)) return false;
  const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
  if (!target) return true;
  return !EDITABLE_TAGS.has(target.tagName ?? '') && target.isContentEditable !== true;
}
